import type { JevQuestion } from "./jev-question.types";
import {
  fieldName,
  isSchemaNode,
  type JevFieldPlan,
  type JevSchemaRefusal,
  type JevTranslation,
  type SchemaNode
} from "./jev-schema-plan.types";
import { ancestorLabel, translateJevLeaf } from "./translate-jev-leaf";

/**
 * The property map at the schema root. A stored `jsonOutputSchema` is either a
 * full object schema or a bare properties map — and in a bare map `type` can be
 * a business FIELD whose value is a field definition, not the keyword (the same
 * reading `findUnsupportedStructuredOutputRoot` makes).
 */
function rootProperties(
  schema: SchemaNode
): { readonly properties: SchemaNode } | { readonly refusal: JevSchemaRefusal } {
  const { type, properties } = schema;
  const typeIsKeyword = typeof type === "string" || Array.isArray(type);

  if (!typeIsKeyword) return { properties: schema };
  if (type === "object" || (Array.isArray(type) && type.includes("object"))) {
    return isSchemaNode(properties)
      ? { properties }
      : {
          refusal: { field: "(root)", reason: "an object schema with no properties asks nothing" }
        };
  }
  return {
    refusal: {
      field: "(root)",
      reason: "the schema root must be an object whose properties are the questions"
    }
  };
}

/**
 * Turn a JSON output schema into Jev questions, or say which fields cannot be.
 *
 * - a string `enum` becomes a Choice over those strings;
 * - a bounded `integer` with 2–10 values becomes a Score whose levels are the
 *   integers, read back as the nearest one;
 * - a bounded `number` becomes a five-anchor Score, read back as the
 *   probability-weighted value in range;
 * - a `boolean` becomes a Noul, read back as `true` when yes is more likely;
 * - a nested `object` recurses, so the answer has the schema's own shape, and
 *   each nested question names its enclosing objects so two leaves sharing a
 *   key under different parents ask different things.
 *
 * Anything else — free text, a list, a union, a `$ref` — is REFUSED, and every refusal is
 * collected rather than stopping at the first, so one round-trip tells the
 * author everything to change. Surface-agnostic: it reads a schema and nothing
 * about who is asking.
 */
export function translateSchemaToJevQuestions(schema: unknown): JevTranslation {
  if (!isSchemaNode(schema)) {
    return { ok: false, refusals: [{ field: "(root)", reason: "the schema must be an object" }] };
  }
  const root = rootProperties(schema);
  if ("refusal" in root) return { ok: false, refusals: [root.refusal] };

  const questions: Record<string, JevQuestion> = {};
  const fields: Record<string, JevFieldPlan> = {};
  const refusals: JevSchemaRefusal[] = [];

  const visit = (
    properties: SchemaNode,
    parent: readonly string[],
    ancestors: readonly string[]
  ): void => {
    const keys = Object.keys(properties);
    if (keys.length === 0) {
      refusals.push({ field: fieldName(parent), reason: "has no properties, so it asks nothing" });
    }
    for (const key of keys) {
      const path = [...parent, key];
      const node = properties[key];
      if (!isSchemaNode(node)) {
        refusals.push({ field: fieldName(path), reason: "is not a schema object" });
        continue;
      }
      const result = translateJevLeaf(node, path, ancestors);
      if (result === "object") {
        const nested = node["properties"];
        if (isSchemaNode(nested)) visit(nested, path, [...ancestors, ancestorLabel(node, key)]);
        else refusals.push({ field: fieldName(path), reason: "is an object with no properties" });
        continue;
      }
      if ("reason" in result) {
        refusals.push(result);
        continue;
      }
      // Positional ids: TypeSafe never shows a key to the model, and a field
      // name may contain any character — the path lives in the plan instead.
      const id = `q${Object.keys(questions).length}`;
      questions[id] = result.question;
      fields[id] = result.plan;
    }
  };

  visit(root.properties, [], []);

  return refusals.length > 0 ? { ok: false, refusals } : { ok: true, questions, fields };
}
